<template>
	<div ref="contentEditorWrapper">
		<div class="font-weight-medium">{{dio.wrapper.docData.editorMetaData.templateName}}</div>
		<v-form ref="form" >
			<v-row>
				<v-col cols="8">
					<v-text-field
						class="mx-3 mt-6"
						label="Name"
						v-model="label"
						:rules="agile.fieldRules.nameRules"
						outlined
						dense
						validate-on-blur
					></v-text-field>
				</v-col>

				<v-col cols="4">
					<v-text-field
						class="mx-3 mt-6"
						label="Slug"
						v-model="slug"
						:rules="agile.fieldRules.nameRules"
						:disabled="!agile.loggedInUser.superAdmin"
						outlined
						dense
						validate-on-blur
					></v-text-field>
				</v-col>
			</v-row>
		</v-form>

		
		<!-- -------------------------------------------- -->
		<!--   Expansion panels -->
		<!-- -------------------------------------------- -->

		<div v-for="(group, i) in uiStructure" :key="i" class="mb-4">
			
			<v-card v-if="group.showAsCard" class="mx-3 px-8 pt-8 pb-3 mb-10">

				<component  v-for="(field, i) in group.fields" :key="i" :field="field" :is="field.uiComponent"  />

			</v-card>

			<v-expansion-panels multiple v-if="!group.showAsCard" class="pl-3" style="padding-right: 12px;">

				<v-expansion-panel>

					<!-- Expanded panel header-->
					<v-expansion-panel-header color="grey lighten-4 primary--text text--darken-1">
						<b>{{ group.name }}</b>
					</v-expansion-panel-header>

					<!-- Expanded panel -->
					<v-expansion-panel-content eager>
						<v-container fluid class="pt-6 pb-0 px-1">
							
							<component  v-for="(field, i) in group.fields" :key="i" :field="field" :is="field.uiComponent"  />
							
						</v-container>
					</v-expansion-panel-content>
				</v-expansion-panel>

			</v-expansion-panels>
		</div>


		<!-- Save button -->
		<v-toolbar flat elevation-1 class="ml-n4 pl-0" style="background: rgba(255, 255, 255, 0.0) !important;">
			<v-spacer/>
			<v-btn class="mr-n4 mb-3" @click="saveDocumentToFirestore">
				Save
			</v-btn>
		</v-toolbar>


	</div>
</template>


<script>

// ----------------------------------------------------
//
//  Main script
//
// ----------------------------------------------------

import agile from "@/shift/agile";
import fb from '@/shift/fb';

// import ViewTableVO from '@/valueobjects/viewseditor/ViewTableVO';
// import ViewCustomObjectsVO from '@/valueobjects/viewseditor/ViewCustomObjectsVO';

import ContentFieldTextField from '@/components/contenteditor/fields/ContentFieldTextField';
import ContentFieldTextArea from '@/components/contenteditor/fields/ContentFieldTextArea';
import ContentFieldNumber from '@/components/contenteditor/fields/ContentFieldNumber';
import ContentFieldInteger from '@/components/contenteditor/fields/ContentFieldInteger';
import ContentFieldBoolean from '@/components/contenteditor/fields/ContentFieldBoolean';
import ContentFieldTable from '@/components/contenteditor/fields/ContentFieldTable';
import ContentFieldRichTextEditor from '@/components/contenteditor/fields/ContentFieldRichTextEditor';
import ContentFieldSimpleFile from '@/components/contenteditor/fields/ContentFieldSimpleFile';
import ContentFieldDynamic from '@/components/contenteditor/fields/ContentFieldDynamic';
import ContentFieldSimpleImage from '@/components/contenteditor/fields/ContentFieldSimpleImage';
import ContentFieldSimpleVideo from '@/components/contenteditor/fields/ContentFieldSimpleVideo';


export default
{
	name: 'ContentEditPage',
	components:
	{
		ContentFieldTextField,
		ContentFieldTextArea,
		ContentFieldNumber,
		ContentFieldInteger,
		ContentFieldBoolean,
		ContentFieldTable,
		ContentFieldRichTextEditor,
		ContentFieldSimpleFile,
		ContentFieldDynamic,
		ContentFieldSimpleImage,
		ContentFieldSimpleVideo
	},


	// ----------------------------------
	//	VUE: Data, properties, etc.
	// ----------------------------------

	props:
	{
		dio: { type: Object }
	},
	
    data: () =>
    ({
		label: '',
		slug: '',
		allowFirestoreSave: true,
		templateGroups: null,

		uiStructure: [],

    }),

	computed:
	{
		agile()
		{
			return agile;
		},
		
		routeKey()
		{
			return this.$route.params.key;
		},
	},

	watch:
	{
		'dio.wrapper.docData.editorMetaData.treeNodeLabel'()
		{
			this.label = this.dio.wrapper.docData.editorMetaData.treeNodeLabel;
		},
	},

	created()
	{
		this.label = this.dio.wrapper.docData.editorMetaData.treeNodeLabel;

		if (!this.slug) this.slug = agile.slugify(this.label);

		this.templateGroups = this.dio.template.link.docData.groups;
		
		for (let i = 0; i < this.templateGroups.length; i++)
		{
			const group =
			{
				name: this.templateGroups[i].name,
				showAsCard: this.templateGroups[i].showAsCard,
				fields: []
			};

			for (let j = 0; j < this.templateGroups[i].fields.length; j++)
			{
				const fieldData = this.templateGroups[i].fields[j];

				if ((fieldData.developersOnly && agile.loggedInUser.currentProject.access.dev) || !fieldData.developersOnly)
				{
					// ----------------------------------------------------
					//	Which type of field?
					// ----------------------------------------------------
					
					let componentName;
					
					switch (fieldData.typeProperties.fieldType)
					{
						case 'textField': componentName = 'ContentFieldTextField'; break;
						case 'textArea': componentName = 'ContentFieldTextArea'; break;
						case 'number': componentName = 'ContentFieldNumber'; break;
						case 'integer': componentName = 'ContentFieldInteger'; break;
						case 'boolean': componentName = 'ContentFieldBoolean'; break;
						//case 'customObjects': componentName = 'ContentFieldCustomObjects'; break;
						case 'table': componentName = 'ContentFieldTable'; break;
						case 'richTextEditor': componentName = 'ContentFieldRichTextEditor'; break;
						case 'simpleFile': componentName = 'ContentFieldSimpleFile'; break;
						case 'dynamicField': componentName = 'ContentFieldDynamic'; break;
						case 'simpleImage': componentName = 'ContentFieldSimpleImage'; break;
						case 'simpleVideo': componentName = 'ContentFieldSimpleVideo'; break;
					}
					

					// ----------------------------------------------------
					//	Continue setup
					// ----------------------------------------------------

					let fieldContentData = null;

					if (this.dio.real.docData.data && this.dio.real.docData.data[fieldData.propertyName])
					{
						fieldContentData = this.dio.real.docData.data[fieldData.propertyName];
					}

					

					const field = 
					{
						actualId: this.dio.wrapper.actualId,
						name: fieldData.name,
						data: fieldContentData,
						template: fieldData,
						value: null, // This is what we want to get back when we save data!
						uiComponent: componentName,
					};

					//ContentEditorTextField
					group.fields.push(field);

				}

			}
			
			if (group.fields.length > 0) this.uiStructure.push(group);
			
		}

	},

    mounted()
    {
		// Save document when pressing "Ctrl+S"
        document.addEventListener("keydown", this.onKeyPressSave);
    },

    methods:
    {
		
		




		onKeyPressSave(e)
		{
			if (!(e.keyCode === 83 && e.ctrlKey))
			{
				return;
			}

			// Make sure we are only saving this document, and not other documents open in the tab menu (thye are hidden).
			// This code below checks if the component is wrapped in an element with display: none; or not
			// Not foolproof though: https://stackoverflow.com/questions/19669786/check-if-element-is-visible-in-dom

			if (this.$refs.contentEditorWrapper.offsetParent)
			{
				this.saveDocumentToFirestore();
			}
			
			e.preventDefault();
			
		},


		// --------------------------------------------------------------------
		//
		//	Save document to Firestore
		//
		// --------------------------------------------------------------------

		saveDocumentToFirestore()
		{
			if (this.$refs.form.validate())
			{
				if (this.allowFirestoreSave)
				{
					this.allowFirestoreSave = false;

					// Protection against spamming firestore, i.e. if the user keeps holding Ctrl+S down
					setTimeout(() =>
					{
						this.allowFirestoreSave = true;
					}, 1000);

					


					const sendData =
					{
						name: this.label,
						slug: this.slug,
						data: {},
					};


					for (let i = 0; i < this.uiStructure.length; i++)
					{
						const groups = this.uiStructure[i];

						for (let j = 0; j < groups.fields.length; j++)
						{
							sendData.data[groups.fields[j].template.propertyName] = (agile.isFunction(groups.fields[j].value)) ? groups.fields[j].value() : groups.fields[j].value ;
						}
					}


					// If we are saving from shared instance in content tree, we need to save to the original ID of the shared document
					const saveToId = (this.dio.contentType === 'shared-content' && this.dio.wrapper.docData.originalId) ? this.dio.wrapper.docData.originalId : this.dio.id ;


					// Batch writes
					const batch = fb.db.batch();

					const doc = fb.db
						.collection('content-collection')
						.doc('content-doc')
						.collection(this.routeKey)
						.doc(this.routeKey)
						.collection(this.dio.contentType)
						.doc(saveToId);
					
					
					

					// ----------------------------------------------------
					//	Wrapper document
					// ----------------------------------------------------
					
					batch.set(doc, 
					{
						slug: this.slug,
						editorMetaData:
						{
							treeNodeLabel: this.label,
							templateClass: this.dio.template.link.docData.className,
						}
					},
					{ merge: true } );


					// ----------------------------------------------------
					//	Main document
					// ----------------------------------------------------
					
					batch.set(doc.collection('document').doc('content'), sendData/* , { merge: true } */ );


					// ----------------------------------------------------
					//	Archive document
					// ----------------------------------------------------

					batch.set(doc.collection('archive').doc(), sendData/* , { merge: true } */ );


					// ----------------------------------------------------
					//	Save data now
					// ----------------------------------------------------

					batch.commit()
					.then(() =>
					{
						this.$root.ai_app.snackbarText = `Saved document ${this.label}`;
						this.$root.ai_app.snackbarTimeout = 4000;
						this.$root.ai_app.snackbar = true;
						//console.log("Updated View Tamplate", this.label);
					})
					.catch((er) =>
					{
						this.$root.$alert("Error updating", er.message);
					});


					
					
					
					
				}
				
			}
		},
	},
	


	beforeDestroy()
	{
		document.removeEventListener("keydown", this.onKeyPressSave);
	},

}

</script>
